// Server clock estimation (§20.5). Ping/pong samples give an offset between the local monotonic
// clock and the server's room tick; the offset of the lowest-RTT recent sample is the target, and
// the applied offset slews toward it once per frame. RTT and jitter are EWMAs; snapshot arrival
// spacing adds to the jitter estimate. A second clock follows snapshot arrivals (the room tick of
// the freshest snapshots arriving now) for the interpolation timeline. DOM-free: every method
// takes the caller's `now` (ms).
import { TICK_MS } from '../core/time';

export const CLOCK_SYNC = {
  initialPings: 5,
  initialPingMs: 200,
  pingMs: 1000,
  /** Pong samples kept; the lowest-RTT one sets the target offset. */
  samples: 8,
  slewTicksPerFrame: 0.05,
  snapTicks: 5,
  ewmaAlpha: 0.2,
  /** Snapshot pairs further apart than this (dropped or paused stream) do not feed jitter. */
  maxArrivalGapTicks: 30,
  /** Snapshot arrivals kept for the arrival clock (≈ 1.3 s at 30 Hz); the fastest one sets it. */
  arrivalSamples: 40,
  /** The arrival clock snaps instead of slewing beyond this error (a new stream, a long stall). */
  arrivalSnapTicks: 8,
} as const;

interface PongSample { rtt: number; offset: number }

export class ClockSync {
  private readonly pongSamples: PongSample[] = [];
  private nextSample = 0;
  private target = 0;
  private applied = 0;
  private hasTarget = false;
  private fromPongs = false;
  private rttAvg = 0;
  private rttJitter = 0;
  private arrivalJitter = 0;
  private pongs = 0;
  private pingsSent = 0;
  private nextPingAt = Infinity;
  private lastArrivalTick = 0;
  private lastArrivalAt = -1;
  private readonly arrivalOffsets = new Float64Array(CLOCK_SYNC.arrivalSamples);
  private arrivalCount = 0;
  private arrivalNext = 0;
  private arrivalTarget = 0;
  private arrivalApplied = 0;

  /** Round-trip time EWMA, ms (0 until the first pong). */
  get rtt(): number {
    return this.rttAvg;
  }

  /** Jitter estimate, ms: EWMA of |rtt − rttAvg| plus EWMA of snapshot arrival deviation (§20.5). */
  get jitter(): number {
    return this.rttJitter + this.arrivalJitter;
  }


  /** True once a pong or snapshot has given an offset. */
  get synced(): boolean {
    return this.hasTarget;
  }

  /** Restarts the ping schedule: 5 pings 200 ms apart, then one every second. */
  startPings(now: number): void {
    this.pingsSent = 0;
    this.nextPingAt = now;
  }

  stopPings(): void {
    this.nextPingAt = Infinity;
  }

  /** True when a ping should be sent now; advances the schedule. */
  pingDue(now: number): boolean {
    if (now < this.nextPingAt) return false;
    this.pingsSent++;
    this.nextPingAt = now + (this.pingsSent < CLOCK_SYNC.initialPings ? CLOCK_SYNC.initialPingMs : CLOCK_SYNC.pingMs);
    return true;
  }

  /** A pong for the ping sent at `c`, received at `now`. The first pong snaps the clock. */
  addPong(c: number, tick: number, frac: number, now: number): void {
    const rtt = Math.max(0, now - c);
    const serverTickAtNow = tick + frac + rtt / 2 / TICK_MS;
    const sample = { rtt, offset: serverTickAtNow - now / TICK_MS };
    if (this.pongSamples.length < CLOCK_SYNC.samples) this.pongSamples.push(sample);
    else this.pongSamples[this.nextSample] = sample;
    this.nextSample = (this.nextSample + 1) % CLOCK_SYNC.samples;

    const a = CLOCK_SYNC.ewmaAlpha;
    if (this.pongs++ === 0) {
      this.rttAvg = rtt;
    } else {
      this.rttJitter += a * (Math.abs(rtt - this.rttAvg) - this.rttJitter);
      this.rttAvg += a * (rtt - this.rttAvg);
    }

    let best = this.pongSamples[0];
    for (const s of this.pongSamples) if (s.rtt < best.rtt) best = s;
    this.target = best.offset;
    if (!this.fromPongs) this.applied = this.target;
    this.fromPongs = true;
    this.hasTarget = true;
  }

  /**
   * A snapshot for `roomTick` arrived at `now`. Feeds arrival jitter, and bootstraps the offset
   * (assuming zero latency) until the first pong arrives.
   */
  addSnapshot(roomTick: number, now: number): void {
    const gap = roomTick - this.lastArrivalTick;
    if (this.lastArrivalAt >= 0 && gap > 0 && gap <= CLOCK_SYNC.maxArrivalGapTicks) {
      const deviation = Math.abs(now - this.lastArrivalAt - gap * TICK_MS);
      this.arrivalJitter += CLOCK_SYNC.ewmaAlpha * (deviation - this.arrivalJitter);
    }
    if (gap > 0 || this.lastArrivalAt < 0) {
      this.lastArrivalTick = roomTick;
      this.lastArrivalAt = now;
    }
    // Arrival clock: the fastest recent arrival (largest roomTick − arrival time) marks where the
    // stream is now; slower arrivals are jitter, which the interpolation delay absorbs.
    this.arrivalOffsets[this.arrivalNext] = roomTick - now / TICK_MS;
    this.arrivalNext = (this.arrivalNext + 1) % CLOCK_SYNC.arrivalSamples;
    if (this.arrivalCount < CLOCK_SYNC.arrivalSamples) this.arrivalCount++;
    let best = -Infinity;
    for (let i = 0; i < this.arrivalCount; i++) best = Math.max(best, this.arrivalOffsets[i]);
    if (this.arrivalCount === 1) this.arrivalApplied = best;
    this.arrivalTarget = best;
    if (this.fromPongs) return;
    const offset = roomTick - now / TICK_MS;
    this.target = this.hasTarget ? Math.max(this.target, offset) : offset;
    if (!this.hasTarget) this.applied = offset;
    this.hasTarget = true;
  }

  /** Once per frame: slew the applied offsets toward their targets (snap beyond the limits). */
  update(): void {
    const err = this.target - this.applied;
    if (Math.abs(err) > CLOCK_SYNC.snapTicks) this.applied = this.target;
    else this.applied += Math.max(-CLOCK_SYNC.slewTicksPerFrame, Math.min(CLOCK_SYNC.slewTicksPerFrame, err));
    if (this.arrivalCount === 0) return;
    const arr = this.arrivalTarget - this.arrivalApplied;
    if (Math.abs(arr) > CLOCK_SYNC.arrivalSnapTicks) this.arrivalApplied = this.arrivalTarget;
    else this.arrivalApplied += Math.max(-CLOCK_SYNC.slewTicksPerFrame, Math.min(CLOCK_SYNC.slewTicksPerFrame, arr));
  }

  /** Fractional server room tick at local time `now`. */
  estServerTick(now: number): number {
    return now / TICK_MS + this.applied;
  }

  /**
   * Fractional room tick of the freshest snapshots arriving at local time `now`: the server tick
   * minus the downstream latency, measured from the arrivals themselves (so an asymmetric link is
   * handled). Before any snapshot, the server tick minus half the RTT. The interpolation timeline
   * runs a delay behind this (§20.8), so remotes are interpolated rather than extrapolated however
   * long the link is.
   */
  arrivalTick(now: number): number {
    const est = this.estServerTick(now);
    if (this.arrivalCount === 0) return est - this.rttAvg / 2 / TICK_MS;
    return Math.min(est, now / TICK_MS + this.arrivalApplied);
  }

  /**
   * How much later than the fastest recent arrival snapshots typically arrive, ticks: the spread
   * between the fastest arrival and a low percentile (the 3rd slowest of the window), so a single
   * straggler does not count. 0 before two snapshots.
   */
  get arrivalSpread(): number {
    const n = this.arrivalCount;
    if (n < 2) return 0;
    const sorted = Array.from(this.arrivalOffsets.subarray(0, n)).sort((a, b) => a - b);
    return sorted[n - 1] - sorted[Math.min(2, n - 1)];
  }

  /** Snaps the applied offsets to their targets (hard resync, §20.5). */
  hardResync(): void {
    this.applied = this.target;
    this.arrivalApplied = this.arrivalTarget;
  }

  /** Forgets everything (new server or room). */
  reset(): void {
    this.pongSamples.length = 0;
    this.nextSample = 0;
    this.target = this.applied = 0;
    this.hasTarget = this.fromPongs = false;
    this.rttAvg = this.rttJitter = this.arrivalJitter = 0;
    this.pongs = this.pingsSent = 0;
    this.nextPingAt = Infinity;
    this.lastArrivalTick = 0;
    this.lastArrivalAt = -1;
    this.arrivalCount = this.arrivalNext = 0;
    this.arrivalTarget = this.arrivalApplied = 0;
  }
}
